import React, { useState, useEffect, useRef } from 'react';
import { Chart } from 'chart.js/auto';
import ClaimsTrendChart from '../components/dashboard/ClaimsTrendChart';
import InteractiveDonutChart from '../components/dashboard/InteractiveDonutChart';
import ModelWiseClaimsGraph from '../components/dashboard/ModelWiseClaimsGraph';
import InteractiveTable from '../components/dashboard/InteractiveTable';
import PredictiveInsightCarousel from '../components/dashboard/PredictiveInsightCarousel';
import { Printer, FileSpreadsheet, Lightbulb } from 'lucide-react';

const Modal = ({ isOpen, onClose, title, children }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-80 backdrop-blur-sm z-50 flex justify-center items-center" onClick={onClose}>
            <div className="bg-surface rounded-none border border-mazda-border w-full max-w-lg mx-4" onClick={e => e.stopPropagation()}>
                <div className="p-6 border-b border-mazda-border flex justify-between items-center">
                    <h5 className="font-light text-sm uppercase tracking-widest text-text-main">{title}</h5>
                    <button onClick={onClose} className="text-text-main font-light text-2xl leading-none p-0 hover:text-text-muted transition-colors">&times;</button>
                </div>
                <div className="p-6 max-h-[60vh] overflow-y-auto">
                    {children}
                </div>
            </div>
        </div>
    );
};
const DashboardCard = ({ title, children, className = '' }) => (
    <div className={`bg-surface rounded-none border border-mazda-border flex flex-col overflow-hidden ${className}`}>
        {/* Added shrink-0 to prevent the header from squishing */}
        <div className="px-6 py-4 border-b border-mazda-border shrink-0">
            <h3 className="text-xs font-light text-text-main uppercase tracking-widest">{title}</h3>
        </div>
        
        {/* Added overflow-auto and min-h-0 */}
        <div className="p-6 flex-grow flex items-center justify-center overflow-auto min-h-0">
            {children}
        </div>
    </div>
);


const DonutStatCard = ({ title, data, totalLabel, totalValue }) => {
    const canvasRef = useRef(null);
    const chartInstance = useRef(null);

    useEffect(() => {
        if (canvasRef.current) {
            if (chartInstance.current) chartInstance.current.destroy();
            
            const ctx = canvasRef.current.getContext('2d');
            chartInstance.current = new Chart(ctx, {
                type: 'doughnut',
                data: {
                    labels: data.map(d => d.label),
                    datasets: [{
                        data: data.map(d => d.value),
                        backgroundColor: data.map(d => d.color),
                        borderWidth: 0,
                        hoverOffset: 4
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    cutout: '75%',
                    plugins: {
                        legend: { display: false },
                        tooltip: { 
                            enabled: true,
                            backgroundColor: '#000000',
                            titleFont: { family: "'Inter', sans-serif", size: 10, weight: '600' },
                            bodyFont: { family: "'Inter', sans-serif", size: 10 },
                            cornerRadius: 0
                        }
                    }
                }
            });
        }
        return () => { if (chartInstance.current) chartInstance.current.destroy(); };
    }, [data]);

    return (
        <DashboardCard title={title} className="h-64">
            <div className="flex items-center w-full h-full min-h-0">
                {/* Left Side: Chart Container */}
                <div className="w-1/2 h-full flex items-center justify-center">
                    {/* FIX: Immediate parent of canvas must be relative. Added shrink-0 so it doesn't get squished */}
                    <div className="relative w-32 h-32 shrink-0">
                        <canvas ref={canvasRef}></canvas>
                        
                        {/* FIX: Moved the labels INSIDE the chart wrapper to ensure perfect centering */}
                        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                            <span className="text-lg font-light tracking-widest text-text-main">{totalValue}</span>
                            <span className="text-[10px] text-text-muted uppercase tracking-widest font-semibold">{totalLabel}</span>
                        </div>
                    </div>
                </div>
                
                {/* Right Side: Legend */}
                {/* FIX: Added overflow-y-auto and max-h-full so the legend scrolls if there are too many items */}
                <div className="w-1/2 pl-4 space-y-4 overflow-y-auto max-h-full py-2">
                    {data.map((item, idx) => (
                        <div key={idx} className="flex flex-col">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-semibold text-text-main">{item.value}</span>
                                <span className="text-[10px] text-text-muted font-semibold">{Math.round((item.value / totalValue) * 100)}%</span>
                            </div>
                            <div className="flex items-center mt-1">
                                <span className="w-2 h-2 rounded-none mr-3 shrink-0" style={{ backgroundColor: item.color }}></span>
                                <span className="text-[10px] text-text-muted uppercase tracking-widest font-semibold truncate">{item.label}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </DashboardCard>
    );
};


const TipsCard = () => (
    <DashboardCard title="iCPS Insights" className="h-64">
        <div className="w-full h-full flex flex-col space-y-4 overflow-y-auto pr-2">
            {/* High Probability Rejection Alert */}
            <div className="bg-transparent p-4 border border-mazda-border">
                <div className="flex items-start">
                    <Lightbulb className="w-4 h-4 text-text-main mt-0.5 mr-3 flex-shrink-0" />
                    <p className="text-[10px] uppercase tracking-widest font-semibold text-text-main leading-relaxed">
                        ML Alert: 12 pending claims flagged with high rejection probability. Review data extraction for missing fields.
                    </p>
                </div>
            </div>

            {/* Processing Efficiency Tip */}
            <div className="bg-transparent p-4 border border-mazda-border">
                <div className="flex items-start">
                    <Lightbulb className="w-4 h-4 text-text-main mt-0.5 mr-3 flex-shrink-0" />
                    <p className="text-[10px] uppercase tracking-widest font-semibold text-text-main leading-relaxed">
                        Workflow Efficiency: Automated validation has reduced claim cycle time by 18% this week.
                    </p>
                </div>
            </div>

            {/* Data Anomaly Tip */}
            <div className="bg-transparent p-4 border border-mazda-border">
                <div className="flex items-start">
                    <Lightbulb className="w-4 h-4 text-text-main mt-0.5 mr-3 flex-shrink-0" />
                    <p className="text-[10px] uppercase tracking-widest font-semibold text-text-main leading-relaxed">
                        Anomaly Detected: Unusual spike in warranty claims for "Drive Train" components in the East Region.
                    </p>
                </div>
            </div>
        </div>
    </DashboardCard>
);

const LandingPage = () => {
    const [year, setYear] = useState("2026");

    const claimsHealthData = [
        { label: 'Approved', value: 1225, color: '#000000' },
        { label: 'Rejected', value: 250, color: '#8B0000' },
        { label: 'Pending', value: 125, color: '#CCCCCC' }
    ];

    const costStatsData = [
        { label: 'Parts', value: 45000, color: '#000000' },
        { label: 'Labor', value: 25000, color: '#333333' },
        { label: 'Other', value: 5000, color: '#CCCCCC' }
    ];

    const trendStatsData = [
        { label: 'This Week', value: 180, color: '#000000' },
        { label: 'Last Week', value: 165, color: '#333333' },
        { label: 'Avg', value: 150, color: '#CCCCCC' }
    ];

    return (
        <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8">
                <DonutStatCard 
                    title="CLAIMS HEALTH"
                    data={claimsHealthData}
                    totalLabel="Total"
                    totalValue={1600}
                />
                <DonutStatCard 
                    title="COST STATISTICS"
                    data={costStatsData}
                    totalLabel="Total $"
                    totalValue={75000}
                />
                <DonutStatCard 
                    title="WEEKLY TRENDS"
                    data={trendStatsData}
                    totalLabel="Current"
                    totalValue={180}
                />
                <TipsCard />
            </div>

            <div className="flex flex-wrap gap-4">
                <button className="btn btn-secondary flex items-center space-x-3">
                    <FileSpreadsheet className="w-4 h-4" />
                    <span>EXPORT TO EXCEL</span>
                </button>
                <button className="btn btn-secondary flex items-center space-x-3">
                    <Printer className="w-4 h-4" />
                    <span>PRINT</span>
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                <div className="lg:col-span-8 bg-surface rounded-none border border-mazda-border p-6">
                    <h3 className="text-xs font-light text-text-main mb-6 uppercase tracking-widest">Claims Trend Analysis</h3>
                    <ClaimsTrendChart setYear={setYear} />
                </div>
                <div className="lg:col-span-4 bg-surface rounded-none border border-mazda-border p-6">
                    <h3 className="text-xs font-light text-text-main mb-6 uppercase tracking-widest">Distribution by Type</h3>
                    <InteractiveDonutChart year={year} />
                </div>
            </div>

            <div className="bg-surface rounded-none border border-mazda-border p-6">
                 <h3 className="text-xs font-light text-text-main mb-6 uppercase tracking-widest">Model Wise Claims</h3>
                <ModelWiseClaimsGraph />
            </div>

            <div className="bg-surface rounded-none border border-mazda-border overflow-hidden">
                <InteractiveTable />
            </div>

            <footer className="text-center py-8 mt-12 border-t border-mazda-border">
                <p className="text-text-muted text-[10px] uppercase tracking-widest font-semibold">&copy; 2025 INTERRAIT | ALL RIGHTS RESERVED</p>
            </footer>
        </div>
    );
}
export default LandingPage;